/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import PropTypes from 'prop-types';

import React, { Component } from 'react';
import { uniqueId } from 'lodash';
import { FlexLayout, FlexItem, ToggleSwitchSection } from '@waca/ca-ui-toolkit';
import stringResource from 'ba-filter-nls';

import NonNumericFilterPane from './NonNumericFilterPane';
import FilterHeader from './FilterHeader';
import FilterFooter from './FilterFooter';
import './NonNumericFilter.scss';

/*
This is a common filter component that allows a filter to be applied using a
slider control. There are also options to enter to/from filter values directly
via text boxes, invert the current selection and specify whether to include
blank values. The filter type can be to switched to a 'list' filter which allows the user
to select/deselectSVGIcon from a list of values.
*/
export default class NonNumericFilter extends Component<any, any> {
	// Define properties and types
	static propTypes = {
		title: PropTypes.string.isRequired,
		onChange: PropTypes.func.isRequired,
		onClear: PropTypes.func.isRequired,
		onApply: PropTypes.func.isRequired,
		onClose: PropTypes.func.isRequired,
		onDone: PropTypes.func.isRequired,
		showCondition: PropTypes.bool,
		allowCondition: PropTypes.bool,
		inverted: PropTypes.bool,
		selectedValuesCount: PropTypes.number,
		loading: PropTypes.bool,
		readOnly: PropTypes.bool,
		allowSearch: PropTypes.bool,
		conditionFlyoutContentContainerId: PropTypes.string,
		hasMoreData: PropTypes.bool,
		showToggle: PropTypes.bool,
		onToggleChange: PropTypes.func,
		isToggled: PropTypes.bool,
		isDirty: PropTypes.bool,
	};

	// Define default property values
	static defaultProps = {
		filterSelectionType: stringResource.get('range'),
		minimumValue: 0,
		maximumValue: 100,
		fromValue: 0,
		toValue: 0,
		includeNulls: false,
		inverted: false,
		decimalPlaces: 5,
		selectedValuesCount: 0,
		loading: false,
		showLabels: true,
		showCondition: undefined,
		readOnly: false,
		allowCondition: true,
		allowSearch: true,
		hasMoreData: false,
		showToggle: false,
	};

	state = {
		isDirty: false,
		// these states are used to determine dirty
		// these values are set in _resetDirty()
		selectedValuesCount: undefined,
		inverted: undefined,
	};

	componentWillMount() {
		this._conditionFlyoutContentContainerId = uniqueId('filter-condition-');
		this._resetDirty();
	}

	componentWillReceiveProps(nextProps) {
		this.setState({
			isDirty:
				this.state.selectedValuesCount !== nextProps.selectedValuesCount ||
				this.state.inverted !== nextProps.inverted,
		});
	}

	getConditionFlyoutContentContainerId = () => {
		const id = this.props.conditionFlyoutContentContainerId;

		if (typeof id === 'undefined' || id === null || id === '') {
			return this._conditionFlyoutContentContainerId;
		}

		return id;
	};

	_conditionFlyoutContentContainerId: string;

	_resetDirty = () => {
		this.setState({
			isDirty: false,
			selectedValuesCount: this.props.selectedValuesCount,
			inverted: this.props.inverted,
		});
	};

	_onChange = result => {
		this.props.onChange(result);
	};

	_onApply = () => {
		this.props.onApply();
		this._resetDirty();
	};

	_onClear = () => {
		this.props.onClear();
	};

	_onClose = () => {
		this.props.onClose();
	};

	_onOk = () => {
		this._onApply();
		this.props.onDone();
	};

	_onCancel = () => {
		this.props.onClose();
	};

	render() {
		const {
			title,
			showCondition,
			loading,
			readOnly,
			allowSearch,
			allowCondition,
			hasMoreData,
			showToggle,
		} = this.props;

		const showClose = true;

		return (
			<FlexLayout
				fullHeight
				direction="column"
				data-tid="nonNumericFilter"
				className="bi-common-NonNumericFilter"
			>
				<FlexItem gutter={[3, 3, 0, 3]}>
					<FilterHeader
						title={title}
						showSearch={false} // always false, controls the icon
						showMenu={false}
						showClose={showClose}
						onClose={this._onClose}
						height={16}
						readOnly={readOnly}
					/>
				</FlexItem>
				{showToggle && (
					<FlexItem gutter={[3, 3, 0, 3]}>
						<ToggleSwitchSection
							label={stringResource.get('allMembers')}
							checked={this.props.isToggled}
							onChange={this.props.onToggleChange}
						/>
					</FlexItem>
				)}
				<FlexItem grow margin={3}>
					<NonNumericFilterPane
						{...this.props}
						onChange={this._onChange}
						allowSearch={allowSearch}
						showCondition={showCondition}
						allowCondition={allowCondition}
						readOnly={readOnly}
						conditionFlyoutContentContainerId={this.getConditionFlyoutContentContainerId()}
						hasMoreData={hasMoreData}
						readOnlyPrefs={this.props.isToggled}
					/>
				</FlexItem>
				<FlexItem gutter={[0, 3, 3, 3]}>
					<FilterFooter
						loading={loading}
						readOnly={readOnly}
						onClear={this._onClear}
						onApply={this._onApply}
						onOk={this._onOk}
						onCancel={this._onCancel}
						dirty={
							(this.props.isDirty === true || this.props.isDirty === false
								? this.props.isDirty
								: this.state.isDirty) || this.props.isToggled
						}
					/>{' '}
				</FlexItem>
			</FlexLayout>
		);
	}
}
